import { useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { isEqual } from 'lodash';
import { useDrawingRoom } from '../contexts/DrawingRoomContext';
import LazyExcalidraw, { type LazyExcalidrawRef } from './LazyExcalidraw';
import type { ExcalidrawElement } from '../types/drawing.types';

// Define types to avoid direct dependency on @excalidraw/excalidraw
type AppState = {
  cursorX?: number;
  cursorY?: number;
  activeTool?: {
    type: string;
  };
  [key: string]: any;
};

type BinaryFiles = Record<string, any>;

// Define the ref type for CollaborativeExcalidraw
export interface CollaborativeExcalidrawRef {
  // You can expose any methods you want to parent components
  getExcalidrawAPI: () => any | null;
}

interface CollaborativeExcalidrawProps {
  // Add any additional props here if needed
}

const CollaborativeExcalidraw = forwardRef<CollaborativeExcalidrawRef, CollaborativeExcalidrawProps>((_props, ref) => {
  const {
    elements = [],
    appState = {},
    handleLocalChange,
    userId,
    isConnected,
    error,
    isLoading
  } = useDrawingRoom();
  
  const excalidrawRef = useRef<LazyExcalidrawRef>(null);
  const isRemoteUpdate = useRef(false);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getExcalidrawAPI: () => excalidrawRef.current?.excalidrawAPI || null
  }), []);

  // Watch for remote updates to elements
  const prevElementsRef = useRef<readonly ExcalidrawElement[]>(elements);
  
  useEffect(() => {
    // Only mark as remote update if elements have actually changed
    if (!isEqual(prevElementsRef.current, elements)) {
      isRemoteUpdate.current = true;
      prevElementsRef.current = elements;
    }
  }, [elements]);

  // Fit to content on initial load and when elements change
  useEffect(() => {
    const api = excalidrawRef.current?.excalidrawAPI;
    if (api && elements && elements.length > 0) {
      // Use a timeout to ensure Excalidraw is ready
      setTimeout(() => {
        try {
          api.scrollToContent(undefined, { fitToContent: true, animate: false });
        } catch (e) {
          // ignore
        }
      }, 50);
    }
  }, [elements]);

  // Handle element changes
  const handleChange = useCallback((newElements: readonly ExcalidrawElement[], newAppState: AppState) => {
    if (!isConnected) return;
    
    // Skip if this is a remote update
    if (isRemoteUpdate.current) {
      isRemoteUpdate.current = false;
      return;
    }
    
    // Use the handleLocalChange from context to handle updates
    handleLocalChange(newElements, newAppState);
  }, [isConnected, handleLocalChange]);
  
  // Handle remote updates
  const prevAppStateRef = useRef(appState);
  
  useEffect(() => {
    // Only mark as remote update if appState has actually changed
    if (!isEqual(prevAppStateRef.current, appState)) {
      isRemoteUpdate.current = true;
      prevAppStateRef.current = appState;
    }
  }, [appState]);

  // Render other users' cursors (simplified for now)
  const renderCustomUI = useCallback(() => {
    // Return empty fragment for now since we don't have users in context
    return <></>;
  }, []);

  // Show loading or error states
  if (isLoading) return <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-50">Loading board...</div>;
  if (error) return <div className="absolute inset-0 flex items-center justify-center bg-red-50 text-red-600 p-4 z-50">Error: {error.message}</div>;

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <LazyExcalidraw
          ref={excalidrawRef}
          initialData={{ elements, appState }}
          onChange={handleChange}
          UIOptions={{
            canvasActions: {
              loadScene: true,
              saveToActiveFile: true,
              toggleTheme: true,
              changeViewBackgroundColor: true,
              clearCanvas: true,
              export: true,
              theme: true,
            },
          }}
          theme="light"
          style={{ width: '100%', height: '100%' }}
        >
          {renderCustomUI()}
        </LazyExcalidraw>
        
        {!isConnected && (
          <div style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            background: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 4,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            zIndex: 10
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#f59e0b'
            }} />
            <span>Connecting to server...</span>
          </div>
        )}
      </div>
    </div>
  );
});

// Set display name for better debugging
CollaborativeExcalidraw.displayName = 'CollaborativeExcalidraw';

export default CollaborativeExcalidraw;
